import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Grid2, Typography } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CustomAccordion from "../../../../components/ui/materialui/customAccordion";
import InputFecha from "../../../../components/inputs/yup/materialui/inputFecha";
import InputSelect from "../../../../components/inputs/yup/materialui/inputSelect";
import MButton from "../../../../components/inputs/yup/materialui/mButton";
import { FormFiltersInterface } from "../interfaces/FormFiltersInterface"
import InputText from '../../../../components/inputs/yup/materialui/inputText';
import { obtenerValorUf } from "../services/serviceArchivoPac";

const diasOptions = [...Array(31)].map((_, i) => ({
  label: `${i + 1}`,
  value: i + 1,
}));

const validationSchema: yup.ObjectSchema<FormFiltersInterface> = yup.object().shape({
  fechaUF: yup
            .date().required("La fecha de UF es obligatoría"),
            // .min(new Date(), "La fecha de UF no puede ser menor a hoy"),
  fechaCobro: yup
                .date().required("La fecha de cobro es obligatoría"),
                // .min(new Date(), "La fecha de cobro no puede ser menor a hoy"),
  diaDescuento: yup.number()
                    .required("Seleccione día de descuento")
                    .min(1, "El día mínimo debe ser 1")
                    .max(31, "El día máximo debe ser 31"),
  valorUF: yup
    .string()
    .nullable()
    .required("El valor de UF es obligatorio")
    .test(
      "not-zero",
      "El valor de UF no puede ser 0",
      (value) => {
        if (!value || value === null || value === undefined || value === '') {
          return false;
        }
        const numValue = parseFloat(value.toString().replace(/,/g, ''));
        return !isNaN(numValue) && numValue !== 0;
      }
    )
});

interface FiltrosComponentProps {
  handleFilter: (formFilters: FormFiltersInterface) => void;
}

export const FiltrosComponent = ({ handleFilter }: FiltrosComponentProps) => {    

  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);
  
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
    trigger
  } = useForm<FormFiltersInterface>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      fechaUF: today,
      fechaCobro: today,

    },
  });

  const fechaUf = watch("fechaUF");
  const fechaCobro = watch("fechaCobro");
  
  const onSubmit = (data: FormFiltersInterface) => handleFilter( { fechaUF: data.fechaUF, fechaCobro: data.fechaCobro, diaDescuento: data.diaDescuento, valorUF: data.valorUF } );

  const handleFechaUfChange = async () => {
    if (!fechaUf) return;
    const fechaCorregida = new Date(fechaUf);
    const valorUfResponse = await obtenerValorUf('UF', fechaCorregida);
    const valorUf = typeof valorUfResponse.data?.result === "number" ? valorUfResponse.data.result : 0;
    setValue('valorUF', valorUf.toString());
    // Disparar validación del campo valorUF después de establecer el valor
    await trigger('valorUF');
  };
  
  useEffect(() => {
    const fetchFechaUF = async () => {
      await handleFechaUfChange();
    };

    fetchFechaUF();
  }, [fechaUf]);

  return (
    <CustomAccordion title="Filtros" expandedDefault>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid2 container spacing={2} sx={{ 
          marginTop: 1,
          alignItems: "center",
        }}>

            <Grid2 size={4}>
                <InputFecha
                  fecha={fechaUf}
                  label="Fecha UF"
                  name="fechaUF"
                  control={control}
                  error={errors.fechaUF}
                />
            </Grid2>

            <Grid2 size={4}>
                <InputFecha
                  fecha={fechaCobro}
                  label="Fecha cobro"
                  name="fechaCobro"
                  control={control}
                  error={errors.fechaCobro}
                />
            </Grid2>

            <Grid2 size={4}>
                <InputSelect
                    name="diaDescuento"
                    control={control}
                    label="Día de descuento"
                    options={diasOptions}
                    error={errors.diaDescuento}
                />
            </Grid2>

            <Grid2 size={4}>
              <InputText 
                name="valorUF" 
                control={control} 
                label="Valor UF" 
                desabilitado 
                error={errors.valorUF}
              />
            </Grid2>
            

        </Grid2>

        {/* Contenedor del Botón */}
        <Grid2 size={{ xs: 8 }} sx={{ 
          marginTop: '10px',
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}>
          <MButton
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<FilterListIcon />}
            sx={{ 
              height: "40px", 
              whiteSpace: "nowrap",
              px: 3
            }}
          >
            Filtrar
          </MButton>
        </Grid2>
      </form>
    </CustomAccordion>
  );
};